import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import isEqual from 'lodash-es/isEqual'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../../constants/common'
import {
  NOTIFICATION_FILE_UPLOAD,
  NOTIFICATION_FILE_UPLOADED_WITH_ERRORS,
} from '../../../../../constants/notification'
import { useInlineNotifications, useNotification } from '../../../../../hooks'
import { getItemsIds } from '../../../../../utils/common'
import {
  getFormNotificationFilesIsUploading,
  getFormNotificationFilesWithErrors,
  getNotificationNoChanged,
} from '../../../../../utils/notification'
import { useFileUpload } from '../../../../File'
import { useFormRequiredNotification } from '../../../../Form'
import { createFeedback, updateFeedback } from '../../../../Newsboard'
import {
  createRating,
  getFeedback,
  updateRating,
} from '../../RatingEditModal.api'
import { RATING_TYPE_OVERALL } from '../../RatingEditModal.constants'

import {
  getFeedbackPayload,
  getFeedbackRatingRequests,
  getIsRatingChanged,
  getRatingEditPermissions,
  parseRatingData,
} from './useUpdateRating.utils'

export function useUpdateRating({
  ratingData,
  requestId,
  onClose,
  onUpdateListData,
}) {
  const { handleAddErrorNotification } = useNotification()
  const { t } = useTranslation()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const feedbackUuid = ratingData?.feedback?.uuid

  const initialFeedbackText = ratingData?.feedback?.text || EMPTY_STRING
  const initialFiles = ratingData?.feedback?.files || EMPTY_LIST

  const { isDescriptionEditingDisabled, isRatingEditingDisabled } =
    getRatingEditPermissions(ratingData)

  const [feedbackText, setFeedbackText] = useState(initialFeedbackText)
  const [files, setFiles] = useState(initialFiles)
  const [rating, setRating] = useState(EMPTY_OBJECT)
  const [initialRating, setInitialRating] = useState(EMPTY_OBJECT)
  const [isLoading, setIsLoading] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const isTextChanged = feedbackText !== initialFeedbackText
  const isFilesChanged = !isEqual(getItemsIds(initialFiles), getItemsIds(files))
  const isFeedbackChanged = isTextChanged || isFilesChanged
  const isRatingChanged = getIsRatingChanged(rating, initialRating)

  const hasEmptyRequired = !feedbackUuid && !rating[RATING_TYPE_OVERALL]?.value

  useFormRequiredNotification({
    hasEmptyRequired,
    removeNotification: handleRemoveNotification,
    setNotification: handleAddNotification,
    submitCount,
  })

  const {
    filesError,
    filesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  function handleDeleteFile(id) {
    if (getItemsIds(files)?.includes(id)) {
      handleExcludeFile(id)
    } else {
      handleRemoveFile(id)
    }
  }

  function handleChangeFeedbackText(value) {
    setFeedbackText(value)
  }

  useEffect(() => {
    if (filesUploading.length === 0) {
      handleRemoveNotification(NOTIFICATION_FILE_UPLOAD)
    }

    if (filesError.length === 0) {
      handleRemoveNotification(NOTIFICATION_FILE_UPLOADED_WITH_ERRORS)
    }
  }, [filesError.length, filesUploading.length])

  useEffect(() => {
    getFeedback({ request: requestId }).then(result => {
      const parsedRating = parseRatingData(result)

      setRating(parsedRating)
      setInitialRating(parsedRating)
    })
  }, [requestId])

  function updateFeedbackRating(feedbackUuid, ratingToSave = rating) {
    const requests = isRatingEditingDisabled
      ? EMPTY_LIST
      : getFeedbackRatingRequests({
          createRating,
          feedbackUuid,
          rating: ratingToSave,
          updateRating,
        })

    return Promise.all(requests)
      .then(() => {
        onClose()
        onUpdateListData()
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function updateFeedbackData(feedbackUuid) {
    if (feedbackUuid && isFeedbackChanged) {
      return updateFeedback(
        feedbackUuid,
        getFeedbackPayload({
          feedbackText,
          files,
          isFilesChanged,
          isTextChanged,
        })
      )
    }
  }

  function addFeedback(overall) {
    return createFeedback({
      ...getFeedbackPayload({
        feedbackText,
        files,
        isFilesChanged,
        isTextChanged,
      }),
      rating: overall.id,
    }).then(({ data }) =>
      updateFeedbackRating(data.uuid, {
        ...rating,
        [RATING_TYPE_OVERALL]: overall,
      })
    )
  }

  function handleSave() {
    if (filesUploading.length > 0) {
      handleAddNotification(getFormNotificationFilesIsUploading(t))

      return
    }

    if (filesError.length > 0) {
      handleAddNotification(getFormNotificationFilesWithErrors(t))

      return
    }

    setSubmitCount(count => count + 1)

    if (hasEmptyRequired) {
      return
    }

    if (feedbackUuid && !isFeedbackChanged && !isRatingChanged) {
      handleAddNotification(getNotificationNoChanged(t))

      return
    }

    setIsLoading(true)

    if (!feedbackUuid) {
      const overall = rating[RATING_TYPE_OVERALL]

      const saveRequest = overall.id
        ? addFeedback(overall)
        : createRating({
            rating_type: RATING_TYPE_OVERALL,
            requests: [requestId],
            value: overall.value,
          }).then(({ data }) => {
            setRating(val => {
              return { ...val, [RATING_TYPE_OVERALL]: data }
            })

            return addFeedback(data)
          })

      return saveRequest
        .catch(() => {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    if (isFeedbackChanged) {
      return updateFeedbackData(feedbackUuid)
        .then(() => {
          return updateFeedbackRating(feedbackUuid)
        })
        .catch(() => {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    return updateFeedbackRating(feedbackUuid).finally(() => {
      setIsLoading(false)
    })
  }

  function handleChangeRating(type, value) {
    setRating(val => {
      return { ...val, [type]: { ...val[type], value } }
    })
  }

  return {
    feedbackText,
    files,
    filesError,
    filesUploading,
    handleChangeFeedbackText,
    handleChangeRating,
    handleDeleteFile,
    handleSave,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
    isDescriptionEditingDisabled,
    isFeedbackChanged,
    isLoading,
    isRatingChanged,
    isRatingEditingDisabled,
    notifications,
    rating,
  }
}
